import { create } from 'zustand';
import { store } from '../data/db.ts';
import { TODO_DOC_ID } from '../todos/store.ts';
import { documentsOf, isDocument, type ArchiveEntry } from '../archive/store.ts';
import { fundeOf, isFund, type Fund } from '../funde/store.ts';
import { createNote, notes as noteOps, setProperty } from '../data/notes.ts';
import { markIndexDirty, onSyncChange, startSync, stopSync, syncNow, syncStatus } from '../data/sync.ts';
import { onDocsChanged } from '../data/docs.ts';
import { linkTarget, resolve, setNavigator, setNotes, setSearcher } from '../data/links.ts';
import { folderByName, setDbActions, setDbData } from '../db/runtime.ts';
import { onIndexReady, refreshIndex, startSearch, stopSearch } from '../search/client.ts';
import { captureVersion, thinVersions, VERSION_IDLE_MS } from '../data/versions.ts';
import { lock } from './auth.ts';
import type { LocalNote } from '../data/types.ts';
import type { Folder } from '../../../shared/protocol.ts';

export type Phase = 'loading' | 'setup' | 'locked' | 'ready';

type State = {
  phase: Phase;
  notes: LocalNote[];
  /** Posteingang und Archiv. Technisch Notizen, aber weder in Liste noch Export. */
  documents: ArchiveEntry[];
  /** Eingeliefertes aus dem versiegelten Eingang — eigener Bereich, wie die Dokumente. */
  funde: Fund[];
  folders: Folder[];
  activeId: string | null;
  query: string;
  folderFilter: string | null;
  showTrash: boolean;
  /** Der Erinnerungsbereich verdrängt die geöffnete Notiz, statt daneben zu stehen. */
  showTodos: boolean;
  /** Posteingang und Archiv, ebenso. */
  showArchive: boolean;
  /** Gesammeltes aus dem versiegelten Eingang. */
  showFunde: boolean;
  density: 'compact' | 'airy';
  /** Minuten bis zur automatischen Sperre; 0 heißt nie. */
  lockAfter: number;
  imageMode: 'small' | 'balanced' | 'original';
  sync: ReturnType<typeof syncStatus>;
  setPhase: (phase: Phase) => void;
  refresh: () => Promise<void>;
  enter: () => Promise<void>;
  leave: () => void;
  open: (id: string | null) => void;
  setQuery: (query: string) => void;
  setFolderFilter: (id: string | null) => void;
  setShowTrash: (show: boolean) => void;
  setShowTodos: (show: boolean) => void;
  setShowArchive: (show: boolean) => void;
  setShowFunde: (show: boolean) => void;
  toggleDensity: () => void;
  setLockAfter: (minutes: number) => Promise<void>;
  setImageMode: (mode: 'small' | 'balanced' | 'original') => Promise<void>;
  create: () => Promise<string>;
  /** Legt eine Notiz mit fertigem Inhalt an — Weg der Schnellaufnahme per Diktat. */
  captureNote: (content: string) => Promise<string>;
  restoreContent: (id: string, content: string) => Promise<void>;
  togglePin: (id: string) => Promise<void>;
  moveToFolder: (id: string, folderId: string | null) => Promise<void>;
  trash: (id: string) => Promise<void>;
  untrash: (id: string) => Promise<void>;
  addFolder: (name: string) => Promise<void>;
  renameFolder: (id: string, name: string) => Promise<void>;
  recolorFolder: (id: string, color: number) => Promise<void>;
  removeFolder: (id: string) => Promise<void>;
  markVersion: (id: string) => Promise<void>;
};

/**
 * Der Suchindex wird nachgeführt, nicht bei jedem Tastendruck. Er ist abgeleitete
 * Information; ein paar hundert Millisekunden Rückstand fallen niemandem auf.
 */
let reindexTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleReindex(notes: LocalNote[], folders: Folder[]): void {
  if (reindexTimer) clearTimeout(reindexTimer);
  reindexTimer = setTimeout(() => {
    reindexTimer = null;
    void refreshIndex(notes, folders);
  }, 800);
}

/** Merkt sich pro Notiz, wann zuletzt getippt wurde — Grundlage der Versions-Schreibpause. */
const idleTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Fünf Minuten ohne Eingabe schließen einen Bearbeitungsabschnitt ab und erzeugen
 * einen Versionsstand (docs/KONZEPT.md, Abschnitt 6.1).
 */
function scheduleVersion(id: string): void {
  const existing = idleTimers.get(id);
  if (existing) clearTimeout(existing);
  idleTimers.set(
    id,
    setTimeout(() => {
      idleTimers.delete(id);
      void (async () => {
        const note = await store.getNote(id);
        if (!note) return;
        await captureVersion(note);
        await thinVersions(id);
        void syncNow();
      })();
    }, VERSION_IDLE_MS),
  );
}

export const useApp = create<State>((set, get) => ({
  phase: 'loading',
  notes: [],
  documents: [],
  funde: [],
  folders: [],
  activeId: null,
  query: '',
  folderFilter: null,
  showTrash: false,
  showTodos: false,
  showArchive: false,
  showFunde: false,
  density: window.matchMedia('(min-width: 900px)').matches ? 'compact' : 'airy',
  lockAfter: 30,
  imageMode: 'balanced',
  sync: syncStatus(),

  setPhase: (phase) => set({ phase }),

  refresh: async () => {
    const [stored, index] = await Promise.all([store.allNotes(), store.getIndex()]);
    // Erinnerungen liegen technisch in einer Notizzeile, sind aber keine Notiz —
    // sie gehören weder in die Liste noch in Suche, Export oder Datenbanken.
    const all = stored.filter((note) => note.id !== TODO_DOC_ID);
    // Dokumente sind keine Notizen: nicht in der Liste, nicht im Export, nicht in
    // Datenbankblöcken. In der Suche sind sie trotzdem — mit eigener Kennzeichnung.
    const notes = all.filter((note) => !isDocument(note) && !isFund(note));
    const documents = documentsOf(all);
    const funde = fundeOf(all);
    const folders = [...index.folders].sort((a, b) => a.order - b.order);
    set({ notes, documents, funde, folders });
    setNotes(notes);
    setDbData(notes, folders);
    scheduleReindex(all, folders);
  },

  enter: async () => {
    set({ lockAfter: await store.getLockAfter(), imageMode: await store.getImageMode() });
    // Der Vault soll den Neustart des Geräts überleben, statt bei Speicherdruck
    // weggeräumt zu werden (docs/KONZEPT.md, Abschnitt 17).
    void navigator.storage?.persist?.().catch(() => undefined);

    const stored = await store.getDensity();
    const density = stored ?? (window.matchMedia('(min-width: 900px)').matches ? 'compact' : 'airy');
    document.documentElement.dataset['density'] = density;
    set({ density });

    await get().refresh();
    set({ phase: 'ready' });
    onSyncChange(() => {
      set({ sync: syncStatus() });
      void get().refresh();
    });
    // Jede Änderung an einem Dokument — eigene wie fremde — aktualisiert die Liste.
    onDocsChanged((noteId) => {
      void get().refresh();
      scheduleVersion(noteId);
    });
    // Der Editor lebt außerhalb von React — Klicks auf Verknüpfungen und Tags kommen
    // über diese beiden Rückrufe zurück in die App.
    setNavigator((target) => {
      const existing = resolve(target);
      if (existing) {
        set({ activeId: existing });
        return;
      }
      // Ein Link auf eine Notiz, die es nicht gibt, legt sie an — mit dem Titel
      // als Überschrift, damit der Link ab sofort auflöst.
      void (async () => {
        const note = await createNote(`# ${linkTarget(target)}\n\n`, get().folderFilter);
        await get().refresh();
        set({ activeId: note.id });
        void syncNow();
      })();
    });
    setSearcher((searchQuery) => set({ query: searchQuery }));

    setDbActions({
      openNote: (id) => set({ activeId: id }),
      setProperty: async (id, key, value) => {
        await setProperty(id, key, value);
        await get().refresh();
        void syncNow();
      },
      // Eine neue Zeile ist eine neue Notiz. Vorbelegt wird, was die Abfrage ohnehin
      // verlangt — sonst würde sie in ihrer eigenen Tabelle nicht auftauchen.
      createRow: async (config, group) => {
        const folderClause = config.from.find((clause) => clause.kind === 'folder');
        const tagClause = config.from.find((clause) => clause.kind === 'tag');
        const folder = folderClause?.kind === 'folder' ? folderByName(folderClause.name) : undefined;

        const properties: string[] = [];
        if (tagClause?.kind === 'tag') properties.push(`tags: [${tagClause.name}]`);
        if (config.group && group) properties.push(`${config.group}: ${group}`);
        for (const filter of config.filters) {
          if (filter.op === '=' && filter.key !== config.group) {
            properties.push(`${filter.key}: ${filter.value}`);
          }
        }
        const frontmatter = properties.length > 0 ? `---\n${properties.join('\n')}\n---\n\n` : '';
        await createNote(`${frontmatter}# Neue Zeile\n\n`, folder?.id ?? null);
        await get().refresh();
        void syncNow();
      },
    });

    startSync();
    startSearch();
    onIndexReady(() => {
      void refreshIndex(get().notes, get().folders);
    });
  },

  leave: () => {
    stopSync();
    stopSearch();
    for (const timer of idleTimers.values()) clearTimeout(timer);
    idleTimers.clear();
    lock();
    set({
      phase: 'locked',
      notes: [],
      documents: [],
      funde: [],
      folders: [],
      activeId: null,
      query: '',
    });
  },

  open: (id) => set({ activeId: id, showTodos: false, showArchive: false, showFunde: false }),
  setShowTodos: (showTodos) =>
    set({
      showTodos,
      showArchive: false,
      showFunde: false,
      activeId: showTodos ? null : get().activeId,
    }),
  setShowArchive: (showArchive) =>
    set({
      showArchive,
      showTodos: false,
      showFunde: false,
      activeId: showArchive ? null : get().activeId,
    }),
  setShowFunde: (showFunde) =>
    set({
      showFunde,
      showTodos: false,
      showArchive: false,
      activeId: showFunde ? null : get().activeId,
    }),
  setQuery: (query) => set({ query }),
  setFolderFilter: (folderFilter) => set({ folderFilter }),
  setShowTrash: (showTrash) => set({ showTrash }),

  setImageMode: async (mode) => {
    set({ imageMode: mode });
    await store.setImageMode(mode);
  },

  setLockAfter: async (minutes) => {
    set({ lockAfter: minutes });
    await store.setLockAfter(minutes);
  },

  toggleDensity: () => {
    const next = get().density === 'compact' ? 'airy' : 'compact';
    set({ density: next });
    document.documentElement.dataset['density'] = next;
    void store.setDensity(next);
  },

  create: async () => {
    const note = await createNote('', get().folderFilter);
    await get().refresh();
    set({ activeId: note.id });
    void syncNow();
    return note.id;
  },

  captureNote: async (content) => {
    const note = await createNote(content, get().folderFilter);
    await get().refresh();
    set({ activeId: note.id, showTodos: false, showArchive: false, showFunde: false });
    void syncNow();
    return note.id;
  },

  restoreContent: async (id, content) => {
    await noteOps.setContent(id, content);
    await get().refresh();
    void syncNow();
  },

  togglePin: async (id) => {
    const note = get().notes.find((n) => n.id === id);
    if (!note) return;
    await noteOps.setPinned(id, !note.pinned);
    await get().refresh();
    void syncNow();
  },

  moveToFolder: async (id, folderId) => {
    await noteOps.setFolder(id, folderId);
    await get().refresh();
    void syncNow();
  },

  trash: async (id) => {
    const note = await store.getNote(id);
    if (note && note.content.trim() !== '') await captureVersion(note);
    await noteOps.remove(id);
    set({ activeId: null });
    await get().refresh();
    void syncNow();
  },

  untrash: async (id) => {
    await noteOps.restore(id);
    await get().refresh();
    void syncNow();
  },

  addFolder: async (name) => {
    const index = await store.getIndex();
    const folders = [
      ...index.folders,
      // Die nächste freie Kategoriefarbe, damit Ordner sich am Ton unterscheiden.
      { id: crypto.randomUUID(), name, order: index.folders.length, color: (index.folders.length % 5) + 1 },
    ];
    await store.setIndex({ ...index, folders });
    markIndexDirty();
    await get().refresh();
    void syncNow();
  },

  renameFolder: async (id, name) => {
    const index = await store.getIndex();
    await store.setIndex({
      ...index,
      folders: index.folders.map((f) => (f.id === id ? { ...f, name } : f)),
    });
    markIndexDirty();
    await get().refresh();
    void syncNow();
  },

  recolorFolder: async (id, color) => {
    const index = await store.getIndex();
    await store.setIndex({
      ...index,
      folders: index.folders.map((folder) => (folder.id === id ? { ...folder, color } : folder)),
    });
    markIndexDirty();
    await get().refresh();
    void syncNow();
  },

  removeFolder: async (id) => {
    const index = await store.getIndex();
    await store.setIndex({ ...index, folders: index.folders.filter((f) => f.id !== id) });
    markIndexDirty();
    for (const note of get().notes.filter((n) => n.folderId === id)) {
      await noteOps.setFolder(note.id, null);
    }
    set({ folderFilter: null });
    await get().refresh();
    void syncNow();
  },

  markVersion: async (id) => {
    const note = await store.getNote(id);
    if (!note) return;
    await captureVersion(note, { labeled: true });
    void syncNow();
  },
}));
